import React, {useState} from "react";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select, {SelectOption} from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Icon from "@amzn/meridian/icon";
import closeTokens from "@amzn/meridian-tokens/base/icon/close-large";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus";
import {colorGray700} from "@amzn/meridian-tokens/base/color";
import {FormattedMessage, injectIntl} from "react-intl";
import {createEWasteItem as createItemApi, EWC_MISMATCH_REASON} from "../../commons/api/CreateEWasteItem";
import {WTN_MSG} from "../../commons/constants/WtnMessageIds";
import {WEIGHT_UNITS, INITIAL_ITEM_FORM} from "../../commons/constants/EWasteConfig";

/**
 * Add-Item Modal opened from a ContainerCard's "Add Items" button.
 * Owns its own form state. Notifies parent through callbacks:
 *   onCreated()  — fired when the API call succeeds, parent should refetch
 *   onCancel()   — fired when the user dismisses the modal
 *   onError(msg) — fired on API failure so the parent can surface an alert
 *
 * The waste code (wasteProfileId) is inherited verbatim from the parent container — there is
 * no waste-code field on this form, so an item can never carry a different code than its
 * container from this UI. The one-container-one-EWC rule is enforced server-side by
 * OculusService (HTTP 400 EWC_MISMATCH), which this modal maps to a specific message.
 */
const CreateItemModal = ({open, container, intl, onCreated, onCancel, onError}) => {
    const [itemForm, setItemForm] = useState(INITIAL_ITEM_FORM);
    const [creating, setCreating] = useState(false);

    if (!open || !container) return null;

    const handleCancel = () => {
        if (creating) return;
        setItemForm(INITIAL_ITEM_FORM);
        onCancel && onCancel();
    };

    const confirmCreate = async () => {
        setCreating(true);
        const locale = (intl.locale || "en-GB").replace(/-/g, "_");
        // The item's waste code is the container's, verbatim (legacy "20 01 36" or compact
        // "200136"), so the server-side equality guard sees identical strings in either era.
        const result = await createItemApi(
            container.id,
            itemForm.itemName,
            container.wasteProfileId,
            Number(itemForm.quantity),
            {weight: Number(itemForm.weightValue), unit: itemForm.weightUnit},
            locale
        );
        setCreating(false);
        if (result.success) {
            setItemForm(INITIAL_ITEM_FORM);
            onCreated && onCreated(result);
        } else {
            // Map a server-side EWC mismatch (HTTP 400) to a specific message; otherwise generic.
            const isMismatch = result.reason === EWC_MISMATCH_REASON;
            const msg = intl.formatMessage({
                id: isMismatch
                    ? WTN_MSG.CONTAINER.ERROR.EWC_MISMATCH
                    : WTN_MSG.CONTAINER.ERROR.CREATE_ITEM_FAILED,
                defaultMessage: isMismatch
                    ? "This item's waste code does not match the container's waste code."
                    : "Failed to add item to container."
            });
            onError && onError(msg);
        }
    };

    const isSaveDisabled = creating
        || !itemForm.itemName
        || !itemForm.quantity
        || !itemForm.weightValue
        || !itemForm.weightUnit
        || !(Number(itemForm.quantity) > 0)
        || !(Number(itemForm.weightValue) > 0);

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            describe="Add item to container"
            width="500px"
            scrollContainer="modal"
        >
            {/* Header */}
            <Row
                alignmentHorizontal="justify"
                alignmentVertical="center"
                spacingInset="medium large"
                backgroundColor={colorGray700}
            >
                <Text type="h200" color="inverted">
                    <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.TITLE} defaultMessage="Add Item to Container" />
                </Text>
                <Button type="icon" onClick={handleCancel}>
                    <Icon tokens={closeTokens}>
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.CANCEL} defaultMessage="Cancel" />
                    </Icon>
                </Button>
            </Row>

            <Column spacingInset="large" spacing="medium">
                <Column spacing="xsmall">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.ITEM_NAME} defaultMessage="Item Name" />
                    </Text>
                    <Input
                        type="text"
                        value={itemForm.itemName}
                        onChange={(value) => setItemForm((prev) => ({...prev, itemName: value}))}
                        placeholder={intl.formatMessage({
                            id: WTN_MSG.CONTAINER.CREATE_ITEM.ITEM_NAME_PLACEHOLDER,
                            defaultMessage: "Enter item name"
                        })}
                        size="small"
                        width="100%"
                    />
                </Column>

                <Column spacing="xsmall">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.QUANTITY} defaultMessage="Quantity" />
                    </Text>
                    <Input
                        type="number"
                        value={itemForm.quantity}
                        onChange={(value) => setItemForm((prev) => ({...prev, quantity: value}))}
                        placeholder={intl.formatMessage({
                            id: WTN_MSG.CONTAINER.CREATE_ITEM.QUANTITY_PLACEHOLDER,
                            defaultMessage: "Enter quantity"
                        })}
                        size="small"
                        width="100%"
                    />
                </Column>

                <Column spacing="xsmall">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.WEIGHT} defaultMessage="Weight" />
                    </Text>
                    <Row spacing="small" widths={["fill", "120px"]}>
                        <Input
                            type="number"
                            value={itemForm.weightValue}
                            onChange={(value) => setItemForm((prev) => ({...prev, weightValue: value}))}
                            placeholder={intl.formatMessage({
                                id: WTN_MSG.CONTAINER.CREATE_ITEM.WEIGHT_PLACEHOLDER,
                                defaultMessage: "Enter weight"
                            })}
                            size="small"
                            width="100%"
                        />
                        <Select
                            value={itemForm.weightUnit}
                            onChange={(value) => setItemForm((prev) => ({...prev, weightUnit: value}))}
                            size="small"
                            width="100%"
                        >
                            {WEIGHT_UNITS.map((u) => (
                                <SelectOption key={u} value={u} label={u} />
                            ))}
                        </Select>
                    </Row>
                </Column>
            </Column>

            <ModalFooter>
                <Row alignmentHorizontal="end" spacing="small">
                    <Button
                        type="secondary"
                        size="small"
                        disabled={creating}
                        onClick={handleCancel}
                    >
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.CANCEL} defaultMessage="Cancel" />
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        disabled={isSaveDisabled}
                        onClick={confirmCreate}
                    >
                        <Icon tokens={plusTokens} />
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE_ITEM.SAVE} defaultMessage="Add Item" />
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default injectIntl(CreateItemModal);



// WEBPACK FOOTER //
// ./src/components/wtn/CreateItemModal.js